import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from '../utils/toast';
import {
  Pen, Type, Eraser, Undo2, Redo2, X, Save, Loader2, Trash2, Compass,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Internal canvas resolution. 1024² is the sweet spot for gpt-image-2
// edits and produces a sharp PNG without a giant payload.
const CANVAS_SIZE = 1024;

/**
 * DirectorSketchModal — fullscreen finger/mouse drawing surface that lets
 * the user sketch a rough floor plan + drop text labels, then sends the PNG
 * + label list to the backend so Opus 4.7 can parse fixtures and gpt-image-2
 * can redraw it as a clean architectural plan.
 *
 * Props:
 *   projectId, locationId, locationName  — context for the API call
 *   onClose()                            — close without saving
 *   onSaved({url, fixtures_count, ...})  — invoked after a successful redraw;
 *                                          parent should refresh geography doc.
 */
export default function DirectorSketchModal({
  projectId,
  locationId,
  locationName,
  onClose,
  onSaved,
}) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const undoStackRef = useRef([]); // ImageData[]  — pre-stroke snapshots
  const redoStackRef = useRef([]);

  const [tool, setTool] = useState('pen'); // 'pen' | 'text' | 'eraser'
  const [labels, setLabels] = useState([]); // [{x_pct, y_pct, text}]
  const [pendingLabel, setPendingLabel] = useState(null); // {x,y,text}
  const [dimHint, setDimHint] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Initialise canvas ────────────────────────────────────────────────
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    cvs.width = CANVAS_SIZE;
    cvs.height = CANVAS_SIZE;
    const ctx = cvs.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.strokeStyle = '#0a0a0a';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctxRef.current = ctx;
  }, []);

  // ── Pointer event helpers ────────────────────────────────────────────
  const getCanvasPoint = useCallback((e) => {
    const cvs = canvasRef.current;
    const rect = cvs.getBoundingClientRect();
    // pointer events report clientX/Y; touch events fall back to first touch.
    const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return {
      x: ((cx - rect.left) / rect.width) * CANVAS_SIZE,
      y: ((cy - rect.top) / rect.height) * CANVAS_SIZE,
    };
  }, []);

  const snapshotForUndo = useCallback(() => {
    if (!ctxRef.current) return;
    const snap = ctxRef.current.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    undoStackRef.current.push(snap);
    if (undoStackRef.current.length > 30) undoStackRef.current.shift();
    redoStackRef.current = []; // any new stroke clears redo history
  }, []);

  // ── Drop a label programmatically (used by Quick-tag chips) ──────────
  // We DO NOT bake into the canvas here — labels stay as draggable overlay
  // divs so the user can move them. They get rasterised into the canvas
  // only at Save time so gpt-image-2 sees them in their final position.
  // Initial position cascades each new label by ~36px so consecutive chip
  // taps don't pile on the same x,y.
  const dropLabelAt = useCallback((text) => {
    if (!text || !text.trim()) return;
    setLabels((prev) => {
      const offset = (prev.length % 12) * 36;
      const cx = CANVAS_SIZE / 2 - 60 + offset;
      const cy = CANVAS_SIZE / 2 - 12 + offset;
      return [
        ...prev,
        {
          id: `lbl_${Date.now()}_${prev.length}`,
          x_pct: (cx / CANVAS_SIZE) * 100,
          y_pct: (cy / CANVAS_SIZE) * 100,
          text: text.trim(),
        },
      ];
    });
  }, []);

  // ── Drawing handlers (pen / eraser) ──────────────────────────────────
  const startStroke = useCallback((e) => {
    if (tool === 'text') {
      // Spawn an inline text input at this canvas position
      const pt = getCanvasPoint(e);
      setPendingLabel({ x: pt.x, y: pt.y, text: '' });
      return;
    }
    snapshotForUndo();
    drawingRef.current = true;
    lastPtRef.current = getCanvasPoint(e);
    e.preventDefault();
  }, [tool, getCanvasPoint, snapshotForUndo]);

  const continueStroke = useCallback((e) => {
    if (!drawingRef.current || !ctxRef.current) return;
    e.preventDefault();
    const pt = getCanvasPoint(e);
    const ctx = ctxRef.current;
    if (tool === 'eraser') {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = 28;
      ctx.beginPath();
      ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
      ctx.restore();
      // Re-fill any erased pixels with white so the export PNG stays white.
      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      ctx.restore();
    } else {
      ctx.lineWidth = 4;
      ctx.strokeStyle = '#0a0a0a';
      ctx.beginPath();
      ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
    }
    lastPtRef.current = pt;
  }, [tool, getCanvasPoint]);

  const endStroke = useCallback(() => {
    drawingRef.current = false;
    lastPtRef.current = null;
  }, []);

  // ── Undo / redo ──────────────────────────────────────────────────────
  const undo = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || undoStackRef.current.length === 0) return;
    const cur = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    redoStackRef.current.push(cur);
    const prev = undoStackRef.current.pop();
    ctx.putImageData(prev, 0, 0);
  }, []);

  const redo = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || redoStackRef.current.length === 0) return;
    const cur = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    undoStackRef.current.push(cur);
    const next = redoStackRef.current.pop();
    ctx.putImageData(next, 0, 0);
  }, []);

  const clearAll = useCallback(() => {
    if (!window.confirm('Erase everything and start over?')) return;
    const ctx = ctxRef.current;
    if (!ctx) return;
    snapshotForUndo();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    setLabels([]);
  }, [snapshotForUndo]);

  // ── Label commit (after user types in the inline input) ─────────────
  // Stays on the draggable-overlay layer (NOT baked into canvas yet).
  const commitPendingLabel = useCallback(() => {
    if (!pendingLabel || !pendingLabel.text.trim()) {
      setPendingLabel(null);
      return;
    }
    setLabels((prev) => [
      ...prev,
      {
        id: `lbl_${Date.now()}_${prev.length}`,
        x_pct: (pendingLabel.x / CANVAS_SIZE) * 100,
        y_pct: (pendingLabel.y / CANVAS_SIZE) * 100,
        text: pendingLabel.text.trim(),
      },
    ]);
    setPendingLabel(null);
    setTool('pen');
  }, [pendingLabel]);

  // ── Drag a label to reposition. Returns pointer handlers for a div. ─
  const labelDragRef = useRef(null); // {id, startClientX, startClientY, startX_pct, startY_pct}
  const startLabelDrag = useCallback((e, label) => {
    e.stopPropagation(); // don't let pointerDown propagate to the canvas pen
    e.preventDefault();
    labelDragRef.current = {
      id: label.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX_pct: label.x_pct,
      startY_pct: label.y_pct,
    };
    e.target.setPointerCapture?.(e.pointerId);
  }, []);
  const moveLabelDrag = useCallback((e) => {
    const d = labelDragRef.current;
    if (!d) return;
    const cvs = canvasRef.current;
    if (!cvs) return;
    const rect = cvs.getBoundingClientRect();
    const dx_pct = ((e.clientX - d.startClientX) / rect.width) * 100;
    const dy_pct = ((e.clientY - d.startClientY) / rect.height) * 100;
    setLabels((prev) =>
      prev.map((l) =>
        l.id === d.id
          ? {
              ...l,
              x_pct: Math.max(0, Math.min(100, d.startX_pct + dx_pct)),
              y_pct: Math.max(0, Math.min(100, d.startY_pct + dy_pct)),
            }
          : l,
      ),
    );
  }, []);
  const endLabelDrag = useCallback(() => {
    labelDragRef.current = null;
  }, []);

  const removeLabel = useCallback((id) => {
    setLabels((prev) => prev.filter((l) => l.id !== id));
  }, []);

  // ── Save: bake labels into the canvas, then PNG-encode + POST ──────
  const handleSave = useCallback(async () => {
    if (saving) return;
    const cvs = canvasRef.current;
    if (!cvs) return;
    setSaving(true);
    try {
      // Bake current label positions onto a CLONE of the canvas so we don't
      // permanently scribble on the user's working copy (they may want to
      // keep iterating after a failed save).
      const exportCvs = document.createElement('canvas');
      exportCvs.width = CANVAS_SIZE;
      exportCvs.height = CANVAS_SIZE;
      const ectx = exportCvs.getContext('2d');
      ectx.drawImage(cvs, 0, 0);
      ectx.font = 'bold 26px ui-sans-serif, system-ui, sans-serif';
      ectx.textBaseline = 'top';
      labels.forEach((l) => {
        const x = (l.x_pct / 100) * CANVAS_SIZE;
        const y = (l.y_pct / 100) * CANVAS_SIZE;
        ectx.strokeStyle = '#ffffff';
        ectx.lineWidth = 5;
        ectx.strokeText(l.text, x, y);
        ectx.fillStyle = '#0a0a0a';
        ectx.fillText(l.text, x, y);
      });
      const dataUrl = exportCvs.toDataURL('image/png');
      const b64 = dataUrl.split(',')[1];
      // Send labels to backend WITHOUT the client-side id field.
      const cleanLabels = labels.map(({ x_pct, y_pct, text }) => ({ x_pct, y_pct, text }));
      const res = await axios.post(
        `${API}/projects/${projectId}/scene-geography/locations/${locationId}/sketch`,
        {
          sketch_b64: b64,
          text_labels: cleanLabels,
          compass_north_deg: 0,
          dim_hint: dimHint || null,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      toast.success(`Architectural plan ready · ${res.data.fixtures_count} fixtures parsed`);
      if (onSaved) onSaved(res.data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [saving, projectId, locationId, labels, dimHint, onClose, onSaved]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (pendingLabel) return; // typing into the label input
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) { e.preventDefault(); redo(); }
      if (e.key === 'p') setTool('pen');
      if (e.key === 't') setTool('text');
      if (e.key === 'e') setTool('eraser');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, pendingLabel]);

  return createPortal(
    <div className="fixed inset-0 z-[2000] flex flex-col bg-[#0A0F1F]" data-testid="director-sketch-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0">
          <Compass className="w-5 h-5 text-cyan-300 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">Sketch the Layout · {locationName}</div>
            <div className="text-[11px] text-white/50">Draw walls + furniture, drop labels, hit Save. Opus parses fixtures · gpt-image-2 cleans the linework.</div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-white/10 text-white/70"
          data-testid="close-director-sketch"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-[#070C18] overflow-x-auto flex-nowrap scrollbar-thin">
        <ToolBtn active={tool === 'pen'} onClick={() => setTool('pen')} testId="tool-pen" icon={<Pen className="w-4 h-4" />}>Pen</ToolBtn>
        <ToolBtn active={tool === 'text'} onClick={() => setTool('text')} testId="tool-text" icon={<Type className="w-4 h-4" />}>Type</ToolBtn>
        <ToolBtn active={tool === 'eraser'} onClick={() => setTool('eraser')} testId="tool-eraser" icon={<Eraser className="w-4 h-4" />}>Eraser</ToolBtn>
        <div className="w-px h-6 bg-white/10 mx-1" />
        <ToolBtn onClick={undo} testId="tool-undo" icon={<Undo2 className="w-4 h-4" />}>Undo</ToolBtn>
        <ToolBtn onClick={redo} testId="tool-redo" icon={<Redo2 className="w-4 h-4" />}>Redo</ToolBtn>
        <ToolBtn onClick={clearAll} testId="tool-clear" icon={<Trash2 className="w-4 h-4" />}>Clear</ToolBtn>
        <div className="w-px h-6 bg-white/10 mx-1" />
        <input
          value={dimHint}
          onChange={(e) => setDimHint(e.target.value)}
          placeholder="Room size hint (e.g. 20x15 ft)"
          className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/50 flex-shrink-0 w-44"
          data-testid="dim-hint-input"
        />
        <div className="flex-1 min-w-[8px]" />
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-shrink-0 px-4 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-[#031018] text-xs font-semibold flex items-center gap-2 active:scale-95"
          data-testid="director-sketch-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Cleaning up…' : 'Save & Convert'}
        </button>
      </div>

      {/* Quick-tag chips — drop common labels with one tap. Useful for room
          names ("Kitchen", "Living Room") and area-of-interest tags
          ("Locker Room Mirror Wall") without having to switch to the Type
          tool + click + type for every single one. */}
      <QuickTagBar onDrop={(text) => dropLabelAt(text)} />

      {/* Canvas surface */}
      <div className="flex-1 overflow-auto bg-[#050912] flex items-center justify-center p-3">
        <div
          className="relative bg-white rounded-lg shadow-2xl"
          style={{ touchAction: 'none', maxWidth: '100%', maxHeight: '100%', aspectRatio: '1 / 1', width: 'min(95vw, 95vh)' }}
        >
          <canvas
            ref={canvasRef}
            data-testid="director-sketch-canvas"
            className={`block w-full h-full rounded-lg ${tool === 'text' ? 'cursor-text' : tool === 'eraser' ? 'cursor-cell' : 'cursor-crosshair'}`}
            onPointerDown={startStroke}
            onPointerMove={continueStroke}
            onPointerUp={endStroke}
            onPointerLeave={endStroke}
            onPointerCancel={endStroke}
          />

          {/* Draggable label overlays — sit above the canvas so they can be
              repositioned. Baked into the canvas only at Save time. */}
          {labels.map((l) => (
            <div
              key={l.id}
              className="absolute group select-none touch-none cursor-move bg-yellow-100/95 border border-yellow-500 rounded shadow px-1.5 py-0.5 text-[13px] font-semibold text-black hover:ring-2 hover:ring-cyan-400 transition-shadow"
              style={{
                left: `${l.x_pct}%`,
                top: `${l.y_pct}%`,
                transform: 'translate(0,0)', // anchor at top-left for parity with canvas baking
              }}
              onPointerDown={(e) => startLabelDrag(e, l)}
              onPointerMove={moveLabelDrag}
              onPointerUp={endLabelDrag}
              onPointerCancel={endLabelDrag}
              data-testid={`label-overlay-${l.id}`}
              title="Drag to move · click × to remove"
            >
              {l.text}
              <button
                onClick={(e) => { e.stopPropagation(); removeLabel(l.id); }}
                className="ml-1 opacity-0 group-hover:opacity-100 text-rose-600 font-bold text-[12px] leading-none align-text-top"
                aria-label={`Remove label ${l.text}`}
                data-testid={`label-remove-${l.id}`}
              >×</button>
            </div>
          ))}

          {/* Inline text-input overlay */}
          {pendingLabel && (
            <input
              autoFocus
              type="text"
              value={pendingLabel.text}
              onChange={(e) => setPendingLabel((p) => ({ ...p, text: e.target.value }))}
              onBlur={commitPendingLabel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitPendingLabel(); }
                if (e.key === 'Escape') setPendingLabel(null);
              }}
              placeholder="label…"
              className="absolute bg-yellow-100 border border-yellow-400 px-1.5 py-0.5 text-sm text-black font-medium outline-none rounded shadow-md"
              style={{
                // Position the input so the canvas-coord origin matches.
                left: `calc(${(pendingLabel.x / CANVAS_SIZE) * 100}% )`,
                top: `calc(${(pendingLabel.y / CANVAS_SIZE) * 100}% )`,
                minWidth: 60,
              }}
              data-testid="pending-label-input"
            />
          )}

          {/* Compass mini-marker (top-right) — purely visual */}
          <div className="absolute top-2 right-2 px-2 py-1 rounded bg-black/60 text-[10px] text-white font-mono pointer-events-none flex items-center gap-1.5">
            <Compass className="w-3 h-3 text-cyan-300" /> N ↑
          </div>
        </div>
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t border-white/10 text-[11px] text-white/40 bg-[#070C18]">
        {labels.length > 0
          ? `${labels.length} label${labels.length > 1 ? 's' : ''} placed · drag to move · hover and click × to remove`
          : 'Tap a chip above to drop a label, then drag it where you want · Pen = P · Eraser = E'}
      </div>
    </div>,
    document.body,
  );
}
// Just the 5 most-common starters. Anything else, the user types into
// the custom field on the right (Enter to drop, → drag to position).
const PRESET_TAGS = ['Door', 'Window', 'Mirror', 'Bench', 'Table'];

function QuickTagBar({ onDrop }) {
  const [custom, setCustom] = useState('');
  const submitCustom = () => {
    const txt = custom.trim();
    if (!txt) return;
    onDrop(txt);
    setCustom('');
  };
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-white/10 bg-[#070C18] overflow-x-auto flex-nowrap scrollbar-thin" data-testid="quick-tag-bar">
      <span className="text-[10px] font-semibold text-white/40 uppercase tracking-wider flex-shrink-0 mr-1">Quick tags →</span>
      {PRESET_TAGS.map((tag) => (
        <button
          key={tag}
          onClick={() => onDrop(tag)}
          className="flex-shrink-0 px-2.5 py-1 rounded-full bg-white/[0.04] hover:bg-cyan-500/15 border border-white/10 hover:border-cyan-400/40 text-[11px] text-white/80 hover:text-cyan-200 whitespace-nowrap transition-all active:scale-95"
          data-testid={`quick-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
        >
          + {tag}
        </button>
      ))}
      <div className="w-px h-5 bg-white/10 mx-1 flex-shrink-0" />
      <input
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitCustom(); } }}
        placeholder="Type your own (e.g. Locker Room Mirror Wall)…"
        className="flex-shrink-0 px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/40 w-64"
        data-testid="quick-tag-custom-input"
      />
      <button
        onClick={submitCustom}
        disabled={!custom.trim()}
        className="flex-shrink-0 px-3 py-1 rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 disabled:opacity-30 border border-cyan-400/40 text-[11px] text-cyan-200 whitespace-nowrap"
        data-testid="quick-tag-add-custom"
      >+ Add</button>
    </div>
  );
}

function ToolBtn({ active, onClick, icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
        active
          ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-400/40'
          : 'bg-white/[0.04] text-white/70 hover:bg-white/10 border border-transparent'
      }`}
    >
      {icon}{children}
    </button>
  );
}
